/**
 * The sidebar's two faces, as pure state: the menu, or the Revit Database panel
 * that takes the whole sidebar over.
 *
 * This replaces `chatRail.ts`. The chat used to carry a rail of its own on the
 * right (item BJ) with the database, the history and a collapse toggle; the
 * team's reading of two sidebars was "one too many", so the rail's contents
 * moved into the left sidebar and its state machine shrank to this. History is
 * the sidebar's Chats section now, the collapse is the sidebar's own
 * (`sidebarStore`), and nothing is resizable — so what is left is one question:
 * is the database open?
 *
 * Pure so the rules can be held by a test rather than by JSX.
 */

export type SidebarView = "nav" | "database";

export function openDatabase(): SidebarView {
  return "database";
}

export function closeDatabase(): SidebarView {
  return "nav";
}

/**
 * The view actually shown, given what the user asked for and what is true now.
 *
 * The database is only ever open when all three hold:
 *
 * - **the chat is the pane on screen** — the panel inserts into the chat's
 *   composer, and beside Skills or Plugins it would be a list with nowhere to go;
 * - **the database is available** — a project is selected and a Revit year is
 *   known, the same gate the button is disabled on;
 * - **the sidebar is shown** — the panel polls the index every few seconds while
 *   mounted, and a poll behind a collapsed sidebar is work for nobody.
 *
 * Anything else falls back to the menu, which is also what makes hiding the
 * sidebar with the panel open close the panel, as the old rail's toggle did.
 */
export function resolveSidebarView(
  view: SidebarView,
  facts: { onChat: boolean; databaseAvailable: boolean; sidebarShown: boolean },
): SidebarView {
  if (view !== "database") return "nav";
  return facts.onChat && facts.databaseAvailable && facts.sidebarShown ? "database" : "nav";
}

/**
 * The same cutoff the stylesheet turns the sidebar into a drawer at (the old
 * web's `md`). One constant for the titlebar's toggle and the shell, so the two
 * cannot disagree about which meaning the button has.
 */
export const SIDEBAR_DRAWER_QUERY = "(max-width: 768px)";

/**
 * Whether the sidebar is on screen: at drawer widths that is the drawer being
 * open, otherwise the persisted preference not being "collapsed".
 */
export function sidebarShown(state: { narrow: boolean; collapsed: boolean; mobileOpen: boolean }): boolean {
  return state.narrow ? state.mobileOpen : !state.collapsed;
}
